import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import QRCode from "qrcode";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  clearMfaPending,
  clearState,
  completeMfaLogin,
  confirmMfaEnrollment,
  resendMfaOtp,
  selectMfaPending,
  startMfaEnrollment
} from "features/auth/auth-slice";
import { MfaMethod } from "services/types/mfa.type";

import MfaBackupCodes from "./mfa-backup-codes.component";

type EnrollStep = "choose" | "verify" | "backup";

const extractTotpSecret = (provisioningUri: string): string | null => {
  const match = provisioningUri.match(/[?&]secret=([^&]+)/);
  return match ? match[1] : null;
};

export default function MfaEnroll() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mfaPending = useAppSelector(selectMfaPending);

  const [step, setStep] = useState<EnrollStep>("choose");
  const [method, setMethod] = useState<MfaMethod>("email_otp");
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [enrollResult, setEnrollResult] = useState<{
    authToken: string;
    backupCodes: string[];
  } | null>(null);

  useEffect(() => {
    if (!mfaPending) {
      navigate("/login", { replace: true });
    } else if (!mfaPending.enrollmentRequired) {
      navigate("/mfa/verify", { replace: true });
    }
  }, [mfaPending, navigate]);

  useEffect(() => {
    if (!provisioningUri) {
      setQrDataUrl(null);
      return;
    }
    QRCode.toDataURL(provisioningUri)
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [provisioningUri]);

  if (!mfaPending || !mfaPending.enrollmentRequired) return null;

  const handleStart = () => {
    setIsSubmitting(true);
    setError("");
    dispatch(startMfaEnrollment({ method }))
      .unwrap()
      .then((data) => {
        setProvisioningUri(data.provisioning_uri ?? null);
        setStep("verify");
        setCode("");
      })
      .catch((rejection: { errorMessage?: string }) => {
        setError(rejection?.errorMessage ?? "Unable to start enrollment.");
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length !== 6 || isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    dispatch(confirmMfaEnrollment({ code, trustDevice }))
      .unwrap()
      .then((data) => {
        setEnrollResult({
          authToken: data.auth_token,
          backupCodes: data.backup_codes ?? []
        });
        setStep("backup");
      })
      .catch((rejection: { errorMessage?: string }) => {
        setError(rejection?.errorMessage ?? "That code didn't work.");
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleResend = () => {
    setInfoMessage("");
    dispatch(resendMfaOtp())
      .unwrap()
      .then(() => setInfoMessage("We sent you a new code."))
      .catch((rejection: { errorMessage?: string }) =>
        setInfoMessage(rejection?.errorMessage ?? "Unable to resend the code.")
      );
  };

  const handleComplete = () => {
    if (!enrollResult) return;
    dispatch(completeMfaLogin({ authToken: enrollResult.authToken }));
  };

  const backToSignIn = () => {
    dispatch(clearMfaPending());
    dispatch(clearState());
    navigate("/login", { replace: true });
  };

  return (
    <Stack gap={2} data-testid="mfa-enroll-page">
      <Typography variant="h5" fontWeight="bold">
        Set up two-step verification
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your practice requires a second verification step at sign-in.
      </Typography>

      {step === "choose" && (
        <Stack gap={2}>
          <FormControl>
            <RadioGroup
              value={method}
              onChange={(event) => setMethod(event.target.value as MfaMethod)}
            >
              <FormControlLabel
                value="email_otp"
                control={<Radio />}
                label={
                  <Stack>
                    <Typography>Email verification codes</Typography>
                    <Typography variant="caption" color="text.secondary">
                      We'll email you a 6-digit code each time you sign in.
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value="totp"
                control={<Radio />}
                sx={{ mt: 1 }}
                label={
                  <Stack>
                    <Typography>Authenticator app</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use an app like Google Authenticator or 1Password to
                      generate codes — works without email access.
                    </Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            onClick={handleStart}
            data-testid="mfa-enroll-continue"
          >
            Continue
          </LoadingButton>
        </Stack>
      )}

      {step === "verify" && (
        <form onSubmit={handleConfirm}>
          <Stack gap={2}>
            {method === "totp" ? (
              <>
                <Typography variant="body2">
                  Scan this QR code with your authenticator app, then enter the
                  6-digit code it shows.
                </Typography>
                {qrDataUrl && (
                  <img
                    src={qrDataUrl}
                    alt="Authenticator app QR code"
                    width={180}
                    height={180}
                    style={{ alignSelf: "center" }}
                  />
                )}
                {provisioningUri && (
                  <Typography variant="caption" color="text.secondary">
                    Can't scan? Enter this key manually:{" "}
                    <code>{extractTotpSecret(provisioningUri) ?? ""}</code>
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2">
                We emailed you a 6-digit code. Enter it below to finish
                enrollment. It expires in 10 minutes.
              </Typography>
            )}

            <TextField
              value={code}
              onChange={(event) => setCode(event.target.value)}
              label="6-digit code"
              autoFocus
              fullWidth
              autoComplete="one-time-code"
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                "data-testid": "mfa-enroll-code-input",
                style: { letterSpacing: "0.5em" }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(event) => setTrustDevice(event.target.checked)}
                />
              }
              label="Trust this device for 30 days"
            />

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={code.length !== 6}
              data-testid="mfa-enroll-verify"
            >
              Verify
            </LoadingButton>

            {method === "email_otp" && (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={handleResend}
                sx={{ width: "fit-content" }}
              >
                Resend code
              </Link>
            )}
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => {
                setStep("choose");
                setError("");
                setInfoMessage("");
              }}
              sx={{ width: "fit-content" }}
            >
              Choose a different method
            </Link>
          </Stack>
        </form>
      )}

      {step === "backup" && enrollResult && (
        <MfaBackupCodes
          codes={enrollResult.backupCodes}
          onConfirm={handleComplete}
        />
      )}

      {Boolean(error) && (
        <Alert icon={false} severity="error" data-testid="mfa-enroll-error">
          {error}
        </Alert>
      )}
      {Boolean(infoMessage) && (
        <Alert icon={false} severity="info">
          {infoMessage}
        </Alert>
      )}

      {step !== "backup" && (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={backToSignIn}
          sx={{ width: "fit-content" }}
        >
          Back to sign in
        </Link>
      )}
    </Stack>
  );
}
